import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import LanguageSelector from '../../../components/Common/LanguageSelector';
import { useLanguage } from '../../../hooks/useLanguage';
import { header as frHeader } from '../../../locales/fr/header';
import { header as arHeader } from '../../../locales/ar/header';
import { header as esHeader } from '../../../locales/es/header';
import RegisterModal from './RegisterModal';

const translations = {
  fr: frHeader,
  ar: arHeader,
  es: esHeader
};

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const t = translations[lang];
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('accueil');
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [dbExists, setDbExists] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['accueil', 'fonctionnalites', 'business-types', 'testimonials', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Vérifier si la base de données existe
    const checkDatabase = async () => {
      try {
        // Utiliser indexedDB.databases() pour lister les bases existantes
        const databases = await indexedDB.databases();
        const exists = databases.some(db => db.name === 'OSC_DataDB');
        console.log('Database exists:', exists);
        setDbExists(exists);        
       
      } catch (error) {
        console.error('Error checking database:', error);
        setDbExists(false);
      }
    };
    checkDatabase();
  }, []);

  const handleLogout = () => {
    logout();
    
  };

  const getLinkClass = (sectionId) => {
    const baseClass = "inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium";
    return activeSection === sectionId
      ? `${baseClass} border-indigo-500 text-gray-900`
      : `${baseClass} border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700`;
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleRegisterSubmit = async (data) => {
    try {
      // La base de données sera initialisée dans RegisterModal
      setIsRegisterModalOpen(false);
      // Rediriger vers la page de connexion
      navigate(`/${lang}/login`);
    } catch (error) {
      console.error('Registration failed:', error);
    }
  };

  // Fonction pour rendre les boutons d'authentification
  const renderAuthButtons = () => {
    if (user) {
      return (
        <div className="ml-3 relative">
          <div className="flex items-center space-x-4">
            <Link
              to={`/${lang}/myapp/dashboard`}
              className="text-gray-700 hover:text-indigo-600"
            >
              {user.firstName} {user.lastName}
            </Link>
            <button
              onClick={handleLogout}
              className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700"
            >
              {t.auth.logout}
            </button>
          </div>
        </div>
      );
    }

    // Si la base n'existe pas, montrer uniquement le bouton d'inscription
    if (!dbExists) {
      return (
        <button
          onClick={() => setIsRegisterModalOpen(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700"
        >
          {t.auth.register}
        </button>
      );
    }

    // Si la base existe, montrer le lien de login
    return (
      <Link
        to={`/${lang}/login`}
        className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700"
      >
        {t.auth.login}
      </Link>
    );
  };

  // Fonction pour rendre les boutons d'authentification mobile
  const renderMobileAuthButtons = () => {
    if (user) {
      return (
        <>
          <Link
            to={`/${lang}/myapp/dashboard`}
            className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700"
          >
            {user.firstName} {user.lastName}
          </Link>
          <button
            onClick={handleLogout}
            className="block w-full text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700"
          >
            {t.auth.logout}
          </button>
        </>
      );
    }

    // Si la base n'existe pas, montrer uniquement le bouton d'inscription
    if (!dbExists) {
      return (
        <button
          onClick={() => {
            setIsRegisterModalOpen(true);
            setIsMenuOpen(false);
          }}
          className="block w-full text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700"
        >
          {t.auth.register}
        </button>
      );
    }

    // Si la base existe, montrer le lien de login
    return (
      <Link
        to={`/${lang}/login`}
        className="block w-full text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700"
        onClick={() => setIsMenuOpen(false)}
      >
        {t.auth.login}
      </Link>
    );
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-10">
            {/* Contact Info */}
            <div className="flex items-center space-x-4 text-sm">
              <a href="tel:+33123456789" className="flex items-center hover:text-indigo-200" aria-label="+33 1 23 45 67 89">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="hidden sm:inline">+33 1 23 45 67 89</span>
              </a>
              <a href="mailto:contact@osc.com" className="flex items-center hover:text-indigo-200" aria-label="contact@osc.com">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="hidden sm:inline">contact@osc.com</span>
              </a>
            </div>

            {/* Language Selector */}
            <div className="flex items-center">
              <LanguageSelector 
                textColor="text-white"
                hoverColor="hover:text-indigo-200"
                bgColor="bg-transparent"
                iconColor="text-white"
                dropdownBgColor="bg-white"
                dropdownTextColor="text-gray-700"
                dropdownHoverBgColor="hover:bg-gray-50"
                dropdownSelectedBgColor="bg-indigo-50"
                dropdownSelectedTextColor="text-indigo-600"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white shadow-lg sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <a href="#accueil" className="flex items-center space-x-2" onClick={() => scrollToSection('accueil')}>
                  <span className="text-3xl font-black bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    OSC
                  </span>
                </a>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                <a href="#fonctionnalites" className={getLinkClass('fonctionnalites')}>
                  {t.navigation.features}
                </a>
                <a href="#business-types" className={getLinkClass('business-types')}>
                  {t.navigation.businessTypes}
                </a>
                <a href="#testimonials" className={getLinkClass('testimonials')}>
                  {t.navigation.testimonials}
                </a>
                <a href="#contact" className={getLinkClass('contact')}>
                  {t.navigation.contact}
                </a>
              </div>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:items-center">
              {renderAuthButtons()}
            </div>
            <div className="-mr-2 flex items-center sm:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
              >
                <span className="sr-only">{t.mobile.menu}</span>
                {!isMenuOpen ? (
                  <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                ) : (
                  <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {isMenuOpen && (
            <div className="sm:hidden">
              <div className="pt-2 pb-3 space-y-1">
                <a
                  href="#fonctionnalites"
                  className="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block w-full text-left pl-3 pr-4 py-2 border-l-4 text-base font-medium"
                >
                  {t.navigation.features}
                </a>
                <a
                  href="#business-types"
                  className="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block w-full text-left pl-3 pr-4 py-2 border-l-4 text-base font-medium"
                >
                  {t.navigation.businessTypes}
                </a>
                <a
                  href="#testimonials"
                  className="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block w-full text-left pl-3 pr-4 py-2 border-l-4 text-base font-medium"
                >
                  {t.navigation.testimonials}
                </a>
                <a
                  href="#contact"
                  className="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block w-full text-left pl-3 pr-4 py-2 border-l-4 text-base font-medium"
                >
                  {t.navigation.contact}
                </a>
              </div>
              <div className="pt-4 pb-3 border-t border-gray-200">
                <div className="space-y-1">
                  {renderMobileAuthButtons()}
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      <RegisterModal
        isOpen={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        onSubmit={handleRegisterSubmit}
      />
    </>
  );
};

export default Header; 